import { useEffect, useMemo, useRef, useState } from 'react';
import type { QueueInfo } from '@/types';
import {
  parseQueueMessage,
  type QueueAssignedPayload,
  type QueueOfferedPayload,
  type QueueOutboundMessage,
} from '@/contracts';
import { useClerkToken } from '@/hooks/useClerkToken';
import { PLAY_CLAW_JWT_TEMPLATE } from '@/lib/auth';
import { getTraceId, logError, logInfo, logWarn } from '@/lib/logger';
import { getFullJitterBackoffDelayMs } from '@/lib/reconnectBackoff';
import { isVideoDebugEnabledFromLocation } from '@/lib/videoDebug';
import { addWsBreadcrumb } from '@/lib/wsErrorBreadcrumbs';

type QueueSocketOptions = {
  ticketId?: string | null;
  enabled: boolean;
  onUpdate?: (queue: QueueInfo) => void;
  onAssigned?: (payload: QueueAssignedPayload) => void;
  onOffered?: (payload: QueueOfferedPayload) => void;
};

const resolveQueueWsUrl = () => {
  const raw = import.meta.env.VITE_QUEUE_WS || import.meta.env.VITE_QUEUE_URL;
  if (!raw) {
    throw new Error('VITE_QUEUE_WS or VITE_QUEUE_URL is required for queue WS.');
  }
  let urlString = raw.trim().replace(/\/$/, '');
  if (!/^wss?:\/\//.test(urlString) && /^https?:\/\//.test(urlString)) {
    urlString = urlString.replace(/^http/, 'ws');
  }
  if (!/^wss?:\/\//.test(urlString)) {
    throw new Error(`VITE_QUEUE_WS must start with ws:// or wss:// (or http(s)://): ${raw}`);
  }
  const url = new URL(urlString);
  if (!url.pathname || url.pathname === '/') {
    url.pathname = '/ws/queue';
  } else if (!url.pathname.endsWith('/ws/queue')) {
    url.pathname = url.pathname.replace(/\/$/, '') + '/ws/queue';
  }
  return url.toString();
};

const QUEUE_DEBUG = import.meta.env.VITE_QUEUE_WS_DEBUG === 'true';
const QUEUE_VIDEO_DEBUG = isVideoDebugEnabledFromLocation();
const QUEUE_RECONNECT_BASE_DELAY_MS = 500;
const QUEUE_RECONNECT_MAX_DELAY_MS = 10000;
const QUEUE_CONNECT_DELAY_MS = 150;

export const useQueueSocket = ({ ticketId, enabled, onUpdate, onAssigned, onOffered }: QueueSocketOptions) => {
  const { getToken, isLoaded, isSignedIn } = useClerkToken(PLAY_CLAW_JWT_TEMPLATE);
  const socketRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reconnectAttemptRef = useRef(0);
  const [connected, setConnected] = useState(false);
  const wsUrl = useMemo(() => resolveQueueWsUrl(), []);
  const traceId = useMemo(() => getTraceId(), []);
  const onUpdateRef = useRef(onUpdate);
  const onAssignedRef = useRef(onAssigned);
  const onOfferedRef = useRef(onOffered);

  useEffect(() => {
    onUpdateRef.current = onUpdate;
  }, [onUpdate]);

  useEffect(() => {
    onAssignedRef.current = onAssigned;
  }, [onAssigned]);

  useEffect(() => {
    onOfferedRef.current = onOffered;
  }, [onOffered]);

  useEffect(() => {
    if (!enabled || !ticketId || !isLoaded || !isSignedIn) {
      if (QUEUE_DEBUG || QUEUE_VIDEO_DEBUG) {
        logInfo('queue.ws.connect.skip', 'skip connect', {
          enabled,
          ticketId: Boolean(ticketId),
          isLoaded,
          isSignedIn,
        });
      }
      return;
    }

    let canceled = false;
    let socket: WebSocket | null = null;
    let connectTimeout: ReturnType<typeof setTimeout> | null = null;

    const clearReconnectTimeout = () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
    };

    const scheduleReconnect = () => {
      if (canceled || reconnectTimeoutRef.current) {
        return;
      }
      const attempt = reconnectAttemptRef.current;
      const delay = getFullJitterBackoffDelayMs({
        attempt,
        baseDelayMs: QUEUE_RECONNECT_BASE_DELAY_MS,
        maxDelayMs: QUEUE_RECONNECT_MAX_DELAY_MS,
      });
      reconnectAttemptRef.current += 1;
      reconnectTimeoutRef.current = setTimeout(() => {
        reconnectTimeoutRef.current = null;
        connect();
      }, delay);
      addWsBreadcrumb({ event: 'reconnect_scheduled', surface: 'queue' });
      if (QUEUE_DEBUG) {
        logInfo('queue.ws.reconnect.scheduled', 'reconnect scheduled', { attempt, delay });
      }
    };

    const sendJson = (payload: QueueOutboundMessage) => {
      if (socket?.readyState === WebSocket.OPEN) {
        addWsBreadcrumb({ event: 'message_out', surface: 'queue', direction: 'out', ws_type: payload.type });
        socket.send(JSON.stringify(payload));
      }
    };

    const connect = () => {
      if (canceled) return;
      if (socketRef.current?.readyState === WebSocket.OPEN) {
        return;
      }
      if (socketRef.current?.readyState === WebSocket.CONNECTING) {
        return;
      }
      addWsBreadcrumb({ event: 'connect', surface: 'queue' });
      socket = new WebSocket(wsUrl);
      socketRef.current = socket;
      if (QUEUE_DEBUG) {
        logInfo('queue.ws.connecting', 'connecting', { wsUrl });
      }

      socket.addEventListener('open', async () => {
        addWsBreadcrumb({ event: 'open', surface: 'queue' });
        if (canceled) return;
        if (QUEUE_DEBUG) {
          logInfo('queue.ws.open', 'open');
        }
        try {
          const token = await getToken();
          if (canceled) return;
          if (token) {
            addWsBreadcrumb({ event: 'auth_send', surface: 'queue' });
            sendJson({ type: 'auth', token, trace_id: traceId });
          }
        } catch (err) {
          addWsBreadcrumb({ event: 'error', surface: 'queue' });
          if (QUEUE_DEBUG) {
            logWarn('queue.ws.auth.failed', 'auth failed', { error: String(err) });
          }
        }
        sendJson({ type: 'subscribe', ticket_id: ticketId });
      });

      socket.addEventListener('message', (event) => {
        if (canceled) return;
        try {
          const raw = JSON.parse(event.data) as unknown;
          addWsBreadcrumb({ event: 'message_in', surface: 'queue', direction: 'in' });
          if (QUEUE_DEBUG) {
            logInfo('queue.ws.message', 'message', { data: raw });
          }

          const parsed = parseQueueMessage(raw);
          if (!parsed.ok) {
            addWsBreadcrumb({ event: 'parse_failed', surface: 'queue', direction: 'in' });
            if (QUEUE_DEBUG) {
              logWarn('queue.ws.parse.failed', 'parse failed', { error: parsed.err });
            }
            return;
          }

          const data = parsed.value;

          if ('type' in data && data.type === 'auth') {
            if (data.status === 'ok') {
              addWsBreadcrumb({ event: 'auth_ok', surface: 'queue' });
              setConnected(true);
              reconnectAttemptRef.current = 0;
              clearReconnectTimeout();
            }
            return;
          }

          if ('type' in data && data.type === 'are-you-there') {
            sendJson({ type: 'heartbeat' });
            return;
          }

          if ('type' in data && data.type === 'queue-update') {
            onUpdateRef.current?.(data.queue);
            return;
          }

          if ('type' in data && data.type === 'queue-assigned') {
            onAssignedRef.current?.(data);
            return;
          }

          if ('type' in data && data.type === 'queue-offered') {
            onOfferedRef.current?.(data);
            return;
          }

          if ('status' in data && data.status === 'assigned') {
            onAssignedRef.current?.({
              type: 'queue-assigned',
              robot_id: data.robot_id,
              session_id: data.session_id,
              lease_token: data.lease_token,
              ticket_id: data.ticket_id,
              room_id: data.room_id,
              lease_expires_at: data.lease_expires_at,
            });
            return;
          }

          if ('status' in data && data.status === 'offered') {
            onOfferedRef.current?.({
              type: 'queue-offered',
              robot_id: data.robot_id,
              session_id: data.session_id,
              ticket_id: data.ticket_id,
              room_id: data.room_id,
              offer_expires_at: data.offer_expires_at,
            });
            return;
          }

          if ('status' in data && data.status === 'queued') {
            onUpdateRef.current?.(data.queue);
          }
        } catch (err) {
          addWsBreadcrumb({ event: 'parse_failed', surface: 'queue', direction: 'in' });
          if (QUEUE_DEBUG) {
            logWarn('queue.ws.parse.failed', 'parse failed', { error: String(err) });
          }
        }
      });

      socket.addEventListener('close', (event) => {
        addWsBreadcrumb({ event: 'close', surface: 'queue', close_code: event.code, close_reason: event.reason });
        if (canceled) return;
        setConnected(false);
        if (QUEUE_DEBUG) {
          logWarn('queue.ws.closed', 'closed');
        }
        scheduleReconnect();
      });

      socket.addEventListener('error', () => {
        addWsBreadcrumb({ event: 'error', surface: 'queue' });
        if (canceled) return;
        setConnected(false);
        if (QUEUE_DEBUG) {
          logError('queue.ws.error', 'error');
        }
        scheduleReconnect();
      });
    };

    reconnectAttemptRef.current = 0;
    clearReconnectTimeout();
    connectTimeout = setTimeout(connect, QUEUE_CONNECT_DELAY_MS);

    return () => {
      canceled = true;
      if (connectTimeout) {
        clearTimeout(connectTimeout);
      }
      clearReconnectTimeout();
      socket?.close();
    };
  }, [enabled, ticketId, wsUrl, getToken, isLoaded, isSignedIn, traceId]);

  return { connected };
};
